import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { usePersona } from '../../../contexts/PersonaContext';
import { useAdminUsers } from '../../../api/admin';
import { useUnreadCounts } from '../../../api/messages';
import { UserRole, UserStatus } from '@spi/shared';
import toast from 'react-hot-toast';

export function MarketplaceNav() {
  const { user, realUser, isAuthenticated, isLoading, logout } = useAuth();
  const { persona, isImpersonating, impersonate, exitPersona } = usePersona();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [personaSearch, setPersonaSearch] = useState('');
  const [personaRoleFilter, setPersonaRoleFilter] = useState<string>('');
  const personaPanelRef = useRef<HTMLDivElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifPanelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const isAdmin = realUser?.role === UserRole.ADMIN;
  const { data: unreadCounts } = useUnreadCounts();
  const totalUnread = unreadCounts?.reduce((sum, c) => sum + c.unreadCount, 0) || 0;

  const { data: personaUsers } = useAdminUsers(isAdmin && personaOpen ? {
    search: personaSearch || undefined,
    role: (personaRoleFilter || undefined) as UserRole | undefined,
    status: UserStatus.APPROVED,
    limit: 10,
  } : { limit: 0 });

  // Close persona panel on outside click
  useEffect(() => {
    if (!personaOpen) return;
    const handler = (e: MouseEvent) => {
      if (personaPanelRef.current && !personaPanelRef.current.contains(e.target as Node)) {
        setPersonaOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [personaOpen]);

  // Close notification panel on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const handlePersonaSelect = async (userId: number) => {
    try {
      await impersonate(userId);
      setPersonaOpen(false);
      setPersonaSearch('');
      toast.success('Now impersonating user');
    } catch {
      toast.error('Failed to impersonate user');
    }
  };

  const handlePersonaExit = () => {
    exitPersona();
    toast.success('Returned to admin view');
  };

  const roleColor = (role: string) => {
    if (role === 'donor') return 'bg-blue-100 text-blue-700';
    if (role === 'recipient') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-600';
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-spi-parchment">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-9 h-9 rounded-xl bg-[#1A3F1E] flex items-center justify-center">
                <svg viewBox="0 0 32 32" className="w-6 h-6">
                  <path d="M9 25 C9 25 10.5 11 21 6 C21 6 23 18 13 25 Z" fill="#4CAF50"/>
                  <path d="M10.5 23.5 Q14 16 20.5 7.5" fill="none" stroke="#1A3F1E" strokeWidth="1.2" strokeLinecap="round"/>
                  <circle cx="16" cy="25.5" r="3.2" fill="#F5A623"/>
                  <path d="M17.8 23 C18 20.5 16.5 19 15 18.5" fill="none" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <span className="text-sm font-semibold text-spi-green-dark hidden sm:block">Second Chance Seed Network</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              <Link
                to="/marketplace"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/marketplace' ? 'bg-spi-green/10 text-spi-green' : 'text-spi-slate hover:text-spi-green hover:bg-spi-seed'
                }`}
              >
                Home
              </Link>
              <Link
                to="/marketplace/projects"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/marketplace/projects') ? 'bg-spi-green/10 text-spi-green' : 'text-spi-slate hover:text-spi-green hover:bg-spi-seed'
                }`}
              >
                Browse Projects
              </Link>
              {!isLoading && isAuthenticated && (
                <>
                  <Link
                    to="/marketplace/dashboard"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive('/marketplace/dashboard') ? 'bg-spi-green/10 text-spi-green' : 'text-spi-slate hover:text-spi-green hover:bg-spi-seed'
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/marketplace/donations"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive('/marketplace/donations') ? 'bg-spi-green/10 text-spi-green' : 'text-spi-slate hover:text-spi-green hover:bg-spi-seed'
                    }`}
                  >
                    Donations
                  </Link>
                  <Link
                    to="/marketplace/map"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive('/marketplace/map') ? 'bg-spi-green/10 text-spi-green' : 'text-spi-slate hover:text-spi-green hover:bg-spi-seed'
                    }`}
                  >
                    Org Map
                  </Link>
                  {user?.role === UserRole.ADMIN && (
                    <Link
                      to="/marketplace/admin"
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive('/marketplace/admin') ? 'bg-spi-green/10 text-spi-green' : 'text-spi-slate hover:text-spi-green hover:bg-spi-seed'
                      }`}
                    >
                      Admin
                    </Link>
                  )}
                </>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* Impersonate switcher (admin only) */}
            {isAuthenticated && isAdmin && (
              <div className="relative" ref={personaPanelRef}>
                <button
                  onClick={() => setPersonaOpen(!personaOpen)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                    isImpersonating
                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      : 'text-spi-slate hover:bg-spi-seed hover:text-spi-green'
                  }`}
                  title="Impersonate user"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7a4 4 0 108 0 4 4 0 00-8 0zM6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
                  </svg>
                  <span className="hidden sm:inline">
                    {isImpersonating ? `${persona?.firstName}` : 'Impersonate'}
                  </span>
                </button>

                {personaOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setPersonaOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-2xl shadow-2xl border border-spi-parchment w-80 max-h-[28rem] flex flex-col overflow-hidden z-50">
                      <div className="p-3 border-b border-spi-parchment">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-spi-charcoal">Impersonate User</h4>
                          <button onClick={() => setPersonaOpen(false)} className="text-spi-slate hover:text-spi-charcoal p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={personaSearch}
                          onChange={(e) => setPersonaSearch(e.target.value)}
                          className="input text-sm py-1.5 w-full"
                          autoFocus
                        />
                        <div className="flex gap-1 mt-2">
                          {['', 'donor', 'recipient'].map((r) => (
                            <button
                              key={r}
                              onClick={() => setPersonaRoleFilter(r)}
                              className={`px-2 py-0.5 rounded-lg text-xs font-medium transition-colors ${
                                personaRoleFilter === r ? 'bg-spi-green text-white' : 'bg-spi-seed text-spi-slate hover:text-spi-charcoal'
                              }`}
                            >
                              {r === '' ? 'All' : r === 'donor' ? 'Donors' : 'Recipients'}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {isImpersonating && (
                          <button
                            onClick={handlePersonaExit}
                            className="w-full text-left px-3 py-2 rounded-lg text-sm bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium transition-colors"
                          >
                            Stop impersonating &rarr; Back to Admin
                          </button>
                        )}

                        {personaUsers?.data.filter((u) => u.id !== realUser!.id).map((u) => (
                          <button
                            key={u.id}
                            onClick={() => handlePersonaSelect(u.id)}
                            className={`w-full text-left px-3 py-2 rounded-lg hover:bg-spi-seed transition-colors ${
                              persona?.id === u.id ? 'bg-spi-seed ring-1 ring-spi-green' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-spi-charcoal">{u.firstName} {u.lastName}</p>
                                <p className="text-xs text-spi-slate">{u.organization?.name || u.email}</p>
                              </div>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${roleColor(u.role)}`}>
                                {u.role}
                              </span>
                            </div>
                          </button>
                        ))}

                        {personaUsers?.data.filter((u) => u.id !== realUser!.id).length === 0 && (
                          <p className="text-xs text-spi-slate text-center py-4">No users found</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Notification bell */}
            {isAuthenticated && !isAdmin && (
              <div className="relative" ref={notifPanelRef}>
                <button
                  onClick={() => setNotifOpen(!notifOpen)}
                  className="relative p-2 rounded-xl text-spi-slate hover:text-spi-green hover:bg-spi-seed transition-colors"
                  title="Notifications"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {totalUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-2xl shadow-2xl border border-spi-parchment w-80 max-h-[24rem] flex flex-col overflow-hidden z-50">
                      <div className="p-3 border-b border-spi-parchment">
                        <h4 className="text-sm font-semibold text-spi-charcoal">Unread Messages</h4>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        {(!unreadCounts || unreadCounts.length === 0) ? (
                          <p className="text-sm text-spi-slate text-center py-8">No unread messages</p>
                        ) : (
                          <div className="p-2 space-y-1">
                            {unreadCounts.map((item) => (
                              <button
                                key={`${item.contextType}-${item.contextId}`}
                                onClick={() => {
                                  setNotifOpen(false);
                                  if (item.contextType === 'donation') {
                                    navigate(`/marketplace/donations/${item.contextId}`);
                                  } else {
                                    // For ask Q&A, navigate to the project (we show label which includes project info)
                                    navigate('/marketplace/projects');
                                  }
                                }}
                                className="w-full text-left px-3 py-2 rounded-lg hover:bg-spi-seed transition-colors flex items-center gap-3"
                              >
                                <div className="flex-shrink-0">
                                  {item.contextType === 'donation' ? (
                                    <svg className="w-4 h-4 text-spi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4 text-spi-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-spi-charcoal truncate">
                                    {item.contextType === 'donation' ? 'Donation' : 'Question'}: {item.label || `#${item.contextId}`}
                                  </p>
                                </div>
                                <span className="flex-shrink-0 min-w-[20px] h-[20px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                                  {item.unreadCount}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {isLoading ? (
              <div className="w-24 h-8" />
            ) : isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-xl hover:bg-spi-seed transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-spi-green flex items-center justify-center text-white text-xs font-bold">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <span className="text-sm font-medium text-spi-charcoal hidden sm:block">
                    {user?.firstName}
                  </span>
                  <svg className="w-4 h-4 text-spi-slate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-spi-parchment z-50 py-1">
                      <div className="px-4 py-2 border-b border-spi-parchment">
                        <p className="text-xs text-spi-slate capitalize">{user?.role}</p>
                      </div>
                      <Link
                        to="/marketplace/profile"
                        className="block px-4 py-2 text-sm text-spi-charcoal hover:bg-spi-seed"
                        onClick={() => setMenuOpen(false)}
                      >
                        Profile
                      </Link>
                      {(user?.role === UserRole.DONOR || user?.role === UserRole.ADMIN) && (
                        <Link
                          to="/marketplace/irs-forms"
                          className="block px-4 py-2 text-sm text-spi-charcoal hover:bg-spi-seed"
                          onClick={() => setMenuOpen(false)}
                        >
                          IRS Forms
                        </Link>
                      )}
                      <button
                        onClick={() => { setMenuOpen(false); logout(); }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Log out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/marketplace/login" className="px-4 py-2 rounded-xl text-sm font-medium text-spi-green hover:bg-spi-seed transition-colors">
                  Log in
                </Link>
                <Link to="/marketplace/apply" className="btn btn-primary text-sm py-2 px-4">
                  Apply
                </Link>
              </div>
            )}

            {/* Mobile menu toggle */}
            <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
